import { ErrorMessage, Skeleton } from '@affine/component';
import { useBackendAvailable } from '@affine/core/components/hooks/use-backend-availability';
import {
  StorageUsageBar,
  storageUsageColor,
} from '@affine/core/components/storage-usage-bar';
import { usePrivateFolderStorage } from '@affine/core/modules/organize';
import { WorkspaceQuotaService } from '@affine/core/modules/quota';
import { isBackendUnavailableError } from '@affine/core/utils/backend-availability';
import { useI18n } from '@affine/i18n';
import { useLiveData, useService } from '@toeverything/infra';
import bytes from 'bytes';
import { useEffect } from 'react';

import * as styles from './storage-progress.css';

const formatStorageBytes = (value: number) => bytes.format(value) ?? '0 B';

export interface StorageProgressProgress {
  upgradable?: boolean;
  onUpgrade: () => void;
}

export const StorageProgress = ({
  onUpgrade: _onUpgrade,
}: StorageProgressProgress) => {
  const t = useI18n();
  const backendAvailable = useBackendAvailable();
  const quota = useService(WorkspaceQuotaService).quota;

  useEffect(() => {
    // revalidate quota to get the latest status
    if (backendAvailable) {
      quota.revalidate();
    }
  }, [backendAvailable, quota]);
  const color = useLiveData(quota.color$);
  const usedFormatted = useLiveData(quota.usedFormatted$);
  const maxFormatted = useLiveData(quota.maxFormatted$);
  const percent = useLiveData(quota.percent$);

  const loading = percent === null;
  const loadError = useLiveData(quota.error$);

  const showOfflineNotice =
    !backendAvailable ||
    (loadError !== null && isBackendUnavailableError(loadError));

  if (loading) {
    if (showOfflineNotice) {
      return (
        <StorageUsageBar
          className={styles.accountStorageUsageBar}
          label={t['com.streamlain.storage.used.hint']()}
          usedText="..."
          limitText="..."
          percent={0}
          color="var(--streamlain-v2-text-tertiary)"
        />
      );
    }
    if (loadError) {
      // TODO(@catsjuice): i18n
      return <ErrorMessage>Load error</ErrorMessage>;
    }
    return <Skeleton height={42} />;
  }

  return (
    <StorageUsageBar
      className={styles.accountStorageUsageBar}
      label={t['com.streamlain.storage.used.hint']()}
      usedText={usedFormatted ?? '...'}
      limitText={maxFormatted ?? '...'}
      percent={percent}
      color={color}
    />
  );
};

export const PrivateFolderStorageInfo = () => {
  const t = useI18n();
  const backendAvailable = useBackendAvailable();
  const privateStorage = usePrivateFolderStorage();

  if (!privateStorage) {
    if (!backendAvailable) {
      return (
        <StorageUsageBar
          className={styles.accountPrivateStorageUsageBar}
          label={t['com.streamlain.settings.account.privateFolder.storage']()}
          usedText="..."
          limitText="..."
          percent={0}
          color="var(--streamlain-v2-text-tertiary)"
        />
      );
    }
    return <Skeleton height={36} />;
  }

  const limitText =
    privateStorage.limitBytes === null
      ? t['com.streamlain.storage.unlimited']()
      : formatStorageBytes(privateStorage.limitBytes);
  const privatePercent =
    privateStorage.limitBytes !== null && privateStorage.limitBytes > 0
      ? Math.min(
          100,
          Math.max(
            0.5,
            Number(
              (
                (privateStorage.usedBytes / privateStorage.limitBytes) *
                100
              ).toFixed(4)
            )
          )
        )
      : 0;

  return (
    <StorageUsageBar
      className={styles.accountPrivateStorageUsageBar}
      label={t['com.streamlain.settings.account.privateFolder.storage']()}
      usedText={formatStorageBytes(privateStorage.usedBytes)}
      limitText={limitText}
      percent={privatePercent}
      color={storageUsageColor(privatePercent)}
    />
  );
};
